import React, {useCallback, useState} from 'react';
import {Box, Button, Typography, Backdrop, CircularProgress, ToggleButton, ToggleButtonGroup} from '@mui/material';
import RefreshIcon from "@mui/icons-material/Refresh";
import {useTranslation} from 'react-i18next';

import LoadingProgressBar from '../components/common/LoadingProgressBar';
import MyHistorySessionsTable, {PAGE_SIZE_OPTIONS} from '../components/my-history/MyHistorySessionsTable';
import MyHistoryShiftsTable from "../components/my-history/MyHistoryShiftsTable";

import {RefreshProvider} from '../contexts/RefreshContext';
import {SIMPLE_TIME_FORMAT} from "../config/DateTimeConstants";
import {NO_SESSION_HISTORY_MESSAGE} from "../helpers/SessionHelper";

import {useCurrentUser} from "../hooks/useCurrentUser";
import {useTimezone} from "../hooks/useTimezone";
import useSessionHistory from '../hooks/useSessionHistory';

const styles = {
    box: {
        container: {
            backgroundColor: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
            padding: '1rem',
            paddingTop: '0.2rem',
            width: '100%',
            '& > *': {
                backgroundColor: '#FFFFFF',
                width: '100%',
            },
        },
        header: {
            alignItems: 'center',
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '1rem',
        },
        progressTrack: {
            width: '100%',
            height: '0.25rem',
        }
    },
    toggleButtonGroup: {
        '& .MuiToggleButton-root': {
            minWidth: '4.5rem',
            height: '2rem',
            paddingInline: '1rem',
        },
        '& .MuiToggleButton-root.Mui-selected': {
            backgroundColor: 'primary.main',
            color: '#ffffff'
        },
        '& .MuiToggleButton-root.Mui-selected:hover': {
            backgroundColor: 'primary.main',
            color: '#ffffff'
        }
    },
    toggleButton: {
        historyType: {
            textTransform: 'none',
        },
    },
    typography: {
        lastRefresh: {
            color: '#757575',
            fontSize: '0.75rem',
        }
    },
    refreshButton: {
        padding: '0 0.4rem',
        width: '2rem',
        minWidth: '2rem',
        height: '2rem'
    },
};

const HISTORY_VIEW = {
    SHIFT: 'shift',
    SESSION: 'session',
};

export default function MyHistoryView() {
    const {t} = useTranslation();
    const {currentUser} = useCurrentUser();
    const {timezone} = useTimezone();
    const performerId = currentUser?.id;

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: PAGE_SIZE_OPTIONS[0],
    });
    const [sortOrder, setSortOrder] = useState([{field: 'sessionDate', sort: 'DESC'}]);
    const [filterModel, setFilterModel] = useState([])
    const [selectedView, setSelectedView] = useState(HISTORY_VIEW.SHIFT);

    const {
        sessions,
        serviceTypes,
        totalSessionsCount,
        lastRefreshTime,
        showProgress,
        handleRefresh,
        handleProgressComplete,
    } = useSessionHistory(performerId, timezone, paginationModel, sortOrder, filterModel);

    const handlePaginationModelChange = useCallback((newPaginationModel) => {
        setPaginationModel(newPaginationModel);
    }, []);

    const handleChange = (event, newView) => {
        if (newView !== null) {
            setSelectedView(newView);
            setPaginationModel((currentPaginationModel) => ({
                ...currentPaginationModel,
                page: 0,
                pageSize: PAGE_SIZE_OPTIONS[0]
            }));
        }
    };

    return (
        <RefreshProvider trigger={lastRefreshTime}>
            <Box sx={styles.box.container}>
                <Box sx={styles.box.progressTrack}>
                    {
                      showProgress
                      &&
                      <>
                          <LoadingProgressBar onComplete={handleProgressComplete}/>
                          <Backdrop sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}} open={showProgress}>
                              <CircularProgress color="inherit"/>
                          </Backdrop>
                      </>
                    }
                </Box>
                <Box sx={styles.box.header}>
                    <Button variant="contained" size="small" sx={styles.refreshButton} onClick={handleRefresh} aria-label="Refresh">
                        <RefreshIcon/>
                    </Button>

                    <ToggleButtonGroup value={selectedView} exclusive onChange={handleChange} sx={styles.toggleButtonGroup}>
                        <ToggleButton value ={HISTORY_VIEW.SHIFT} sx={styles.toggleButton.historyType}>
                            {t("myHistoryView.shiftHistory")}
                        </ToggleButton>

                        <ToggleButton value={HISTORY_VIEW.SESSION} sx={styles.toggleButton.historyType}>
                            {t("myHistoryView.sessionHistory")}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {
                    selectedView === HISTORY_VIEW.SESSION
                    ?
                    <MyHistorySessionsTable
                        sessions={sessions}
                        serviceTypes={serviceTypes}
                        noRowsMessage={NO_SESSION_HISTORY_MESSAGE}
                        rowCount={totalSessionsCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={handlePaginationModelChange}
                        setSortOrder={setSortOrder}
                        setFilterModel={setFilterModel}/>
                    :
                    <MyHistoryShiftsTable
                        performerId={performerId}
                        paginationModel={paginationModel}
                        onPaginationModelChange={handlePaginationModelChange}/>
                }
            </Box>
        </RefreshProvider>
    );
}